import { inject, Injectable } from "@angular/core";
import { ShipmentApi } from "../Api/ShipmentApi";
import { ShipmentState } from "./ShipmentState";
import { ShipmentSearchRequest } from "../Dto/ShipmentSearchRequest";
import {
  CreateShipmentRequest,
  ShipmentBoxUpsertRequest,
  ShipmentDeliverRequest,
  ShipmentDispatchRequest,
  UpdateShipmentRequest,
} from "../Dto/CreateShipmentRequest";
import { ShipmentEntity } from "../Dto/ShipmentModel";
import { createBackendError, getBackendErrorMessage } from "../../../Common/Utility/UtilityError";

@Injectable({ providedIn: "root" })
export class ShipmentEffects {
  private readonly shipmentApi = inject(ShipmentApi);
  private readonly shipmentState = inject(ShipmentState);

  async searchShipments(params: ShipmentSearchRequest = {}): Promise<void> {
    const merged: ShipmentSearchRequest = { page: 1, limit: 10, ...params };
    this.shipmentState.setLoading(true);
    this.shipmentState.setError(null);
    try {
      const response = await this.shipmentApi.searchShipments(merged);
      this.shipmentState.setShipments(response.data, response.pagination);
      this.shipmentState.setSearchParams(merged);
    } catch (exception: unknown) {
      this.shipmentState.setError(getBackendErrorMessage(exception));
    } finally {
      this.shipmentState.setLoading(false);
    }
  }

  async createShipment(payload: CreateShipmentRequest): Promise<ShipmentEntity> {
    try {
      const created = await this.shipmentApi.createShipment(payload);
      this.shipmentState.addShipment(created);
      return created;
    } catch (exception: unknown) {
      this.shipmentState.setError(getBackendErrorMessage(exception));
      throw createBackendError(exception);
    }
  }

  async getShipmentById(id: string): Promise<ShipmentEntity | null> {
    try {
      return await this.shipmentApi.getShipmentById(id);
    } catch (exception: unknown) {
      this.shipmentState.setError(getBackendErrorMessage(exception));
      throw createBackendError(exception);
    }
  }

  async updateShipment(id: string, payload: UpdateShipmentRequest): Promise<ShipmentEntity> {
    try {
      const updated = await this.shipmentApi.updateShipment(id, payload);
      this.shipmentState.updateShipment(updated);
      return updated;
    } catch (exception: unknown) {
      this.shipmentState.setError(getBackendErrorMessage(exception));
      throw createBackendError(exception);
    }
  }

  async deleteShipment(id: string): Promise<void> {
    try {
      await this.shipmentApi.deleteShipment(id);
      this.shipmentState.removeShipment(id);
    } catch (exception: unknown) {
      this.shipmentState.setError(getBackendErrorMessage(exception));
      throw createBackendError(exception);
    }
  }

  async addBox(id: string, payload: ShipmentBoxUpsertRequest): Promise<ShipmentEntity> {
    try {
      const updated = await this.shipmentApi.addBox(id, payload);
      this.shipmentState.updateShipment(updated);
      return updated;
    } catch (exception: unknown) {
      this.shipmentState.setError(getBackendErrorMessage(exception));
      throw createBackendError(exception);
    }
  }

  async updateBox(id: string, boxId: string, payload: ShipmentBoxUpsertRequest): Promise<ShipmentEntity> {
    try {
      const updated = await this.shipmentApi.updateBox(id, boxId, payload);
      this.shipmentState.updateShipment(updated);
      return updated;
    } catch (exception: unknown) {
      this.shipmentState.setError(getBackendErrorMessage(exception));
      throw createBackendError(exception);
    }
  }

  async deleteBox(id: string, boxId: string): Promise<ShipmentEntity> {
    try {
      const updated = await this.shipmentApi.deleteBox(id, boxId);
      this.shipmentState.updateShipment(updated);
      return updated;
    } catch (exception: unknown) {
      this.shipmentState.setError(getBackendErrorMessage(exception));
      throw createBackendError(exception);
    }
  }

  async dispatchShipment(id: string, payload: ShipmentDispatchRequest): Promise<ShipmentEntity> {
    try {
      const updated = await this.shipmentApi.dispatchShipment(id, payload);
      this.shipmentState.updateShipment(updated);
      return updated;
    } catch (exception: unknown) {
      this.shipmentState.setError(getBackendErrorMessage(exception));
      throw createBackendError(exception);
    }
  }

  async deliverShipment(id: string, payload: ShipmentDeliverRequest): Promise<ShipmentEntity> {
    try {
      const updated = await this.shipmentApi.deliverShipment(id, payload);
      this.shipmentState.updateShipment(updated);
      return updated;
    } catch (exception: unknown) {
      this.shipmentState.setError(getBackendErrorMessage(exception));
      throw createBackendError(exception);
    }
  }

  async downloadBoxLabelPdf(id: string, boxId: string): Promise<Blob> {
    try {
      return await this.shipmentApi.downloadBoxLabelPdf(id, boxId);
    } catch (exception: unknown) {
      this.shipmentState.setError(getBackendErrorMessage(exception));
      throw createBackendError(exception);
    }
  }

  async downloadShipmentLabelsPdf(id: string): Promise<Blob> {
    try {
      return await this.shipmentApi.downloadShipmentLabelsPdf(id);
    } catch (exception: unknown) {
      this.shipmentState.setError(getBackendErrorMessage(exception));
      throw createBackendError(exception);
    }
  }
}
